/**
 * silenceResizeObserver.js — 2026-07-30 CHG-046 P2 hotfix (demo emergency).
 *
 * The original CHG-046 fix registered a bubble-phase `error` listener AFTER
 * CRA's react-error-overlay had already installed its own capture-phase
 * handler. Result: the overlay still fired even though `defaultPrevented`
 * came back `true` in synthetic tests. This rewrite lands three defences
 * simultaneously (industry-standard pattern used by Radix / MUI / Chakra):
 *
 *   A) Wrap the native ResizeObserver constructor so the "loop completed"
 *      condition can never be raised — every callback runs inside a
 *      `requestAnimationFrame` de-bounce and any thrown loop-error is
 *      swallowed at source. Prevents the browser from firing at all.
 *   B) Install `error` + `unhandledrejection` listeners in CAPTURE phase
 *      (`{capture: true}`) so they run BEFORE react-error-overlay. Handler
 *      chain: stopImmediatePropagation → preventDefault → return false.
 *   C) (Handled in `index.js`, not here.) Patch `console.error` to drop
 *      any string starting with "ResizeObserver loop" — react-error-overlay
 *      hooks console.error too, not just window.onerror.
 *
 * NON-goals — this file must NOT swallow anything besides the ResizeObserver
 * loop message. Every other error surfaces normally.
 */

// Both known message variants — Chromium says "loop completed with
// undelivered notifications", Firefox occasionally says "loop limit
// exceeded". Match either, and nothing else.
export const RESIZE_OBSERVER_ERROR_RE =
    /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/;

export function isBenignResizeObserverError(message) {
    return typeof message === "string" && RESIZE_OBSERVER_ERROR_RE.test(message);
}

function _isROLoopMessage(msg) {
    if (!msg) return false;
    if (typeof msg === "string") return isBenignResizeObserverError(msg);
    return isBenignResizeObserverError(msg.message);
}

// ── Part A · Wrap the native constructor ──────────────────────────────
// Runs at MODULE LOAD (before any observer is created anywhere in the
// bundle). Idempotent — a second load is a no-op because the wrapper's
// `.name` differs from the native constructor's.
if (typeof window !== "undefined"
    && typeof window.ResizeObserver === "function"
    && window.ResizeObserver.name !== "DebouncedResizeObserver") {
    const NativeResizeObserver = window.ResizeObserver;

    class DebouncedResizeObserver extends NativeResizeObserver {
        constructor(callback) {
            let rafId = 0;
            const wrapped = (entries, observer) => {
                if (rafId) cancelAnimationFrame(rafId);
                rafId = requestAnimationFrame(() => {
                    try {
                        callback(entries, observer);
                    } catch (err) {
                        if (_isROLoopMessage(err)) return; // swallow at source
                        throw err;
                    }
                });
            };
            super(wrapped);
        }
    }

    // Preserve the class name so future idempotency checks work.
    Object.defineProperty(DebouncedResizeObserver, "name", { value: "DebouncedResizeObserver" });
    window.ResizeObserver = DebouncedResizeObserver;
}

// ── Part B · Capture-phase error listeners ────────────────────────────
// The handlers are exported as pure functions so the unit test can call
// them directly without dispatching real `ErrorEvent`s (jsdom's synthetic
// dispatch handles those inconsistently). Each returns true if the event
// was suppressed, false otherwise.

/**
 * Handler for `window.addEventListener('error', ..., true)`. Order matters —
 * stopImmediatePropagation MUST run first so any react-error-overlay handler
 * registered AFTER us (also in capture) never receives the event.
 * preventDefault marks the event as handled so the browser doesn't log it.
 */
export function handleError(event) {
    if (!event) return false;
    if (!_isROLoopMessage(event.message) && !_isROLoopMessage(event.error)) return false;
    if (typeof event.stopImmediatePropagation === "function") event.stopImmediatePropagation();
    if (typeof event.preventDefault === "function") event.preventDefault();
    return true;
}

/**
 * Handler for `window.addEventListener('unhandledrejection', ..., true)`.
 * The warning can surface as a rejection when an observer callback runs
 * inside a micro-task. Same filter, promise-shaped input.
 */
export function handleUnhandledRejection(event) {
    if (!event || !_isROLoopMessage(event.reason)) return false;
    if (typeof event.stopImmediatePropagation === "function") event.stopImmediatePropagation();
    if (typeof event.preventDefault === "function") event.preventDefault();
    return true;
}

// Idempotent bootstrap via a well-known window flag so hot-reload doesn't
// stack multiple listeners.
const _INSTALLED_FLAG = "__scope_resizeObserverSilencer_v2__";

export default function installResizeObserverSilencer() {
    if (typeof window === "undefined") return;
    if (window[_INSTALLED_FLAG]) return;
    window[_INSTALLED_FLAG] = true;

    // CAPTURE-phase — critical. Without `true`, react-error-overlay wins.
    window.addEventListener("error", handleError, true);
    window.addEventListener("unhandledrejection", handleUnhandledRejection, true);
}

// Test-only escape hatch so the unit test can reset the guard between runs.
export function __resetForTests() {
    if (typeof window !== "undefined") delete window[_INSTALLED_FLAG];
}

// Convenience: install synchronously on module load in addition to the
// exported bootstrap. This closes the tiny gap between module eval and
// the explicit call in index.js.
installResizeObserverSilencer();
